import {
  useReactMutation,
  useTranslation,
  getPostServicePromise
} from '@invision/core';
import type {
  SecurityUpdateCredentialsRequest,
  SecurityUpdateCredentialsResponse,
  SecurityUpdateCredentialsFaults
} from 'invApi';

// Extended request type to include optional headers
interface UpdateCredentialsRequestWithHeaders {
  request: SecurityUpdateCredentialsRequest;
  headers?: Record<string, string | undefined>;
}

/**
 * Hook for updating user password
 * Supports both authenticated and token-based password updates
 *
 * API: POST Security/UpdateCredentials
 *
 * For authenticated access:
 * - Payload: { CurrentPassword, NewPassword }
 * - Headers: Uses existing session
 *
 * For token-based access (password reset):
 * - Payload: { NewPassword, ResetToken }
 * - Headers: Custom SessionId/SystemId passed via headers parameter
 */
export function useUpdateCredentialsMutation() {
  const { t } = useTranslation();

  return useReactMutation<
    SecurityUpdateCredentialsResponse,
    UpdateCredentialsRequestWithHeaders
  >(
    ['updateCredentials'],
    ({ request, headers }: UpdateCredentialsRequestWithHeaders) =>
      getPostServicePromise(
        { path: 'Security/UpdateCredentials', headers: headers || {} },
        {
          payload: request,
          faults: {
            '111': t('User not found'),
            '116': t('New password cannot be the same as current password'),
            '121': t('Password is locked and cannot be changed'),
            '123': t('Current password is incorrect'),
            '154': t('Invalid or missing reset token'),
            '158': t('Reset token has expired'),
            '159': t('Invalid update credentials request'),
            '115-11501': t('New password does not meet requirements')
          } as SecurityUpdateCredentialsFaults
        }
      ),
    {},
    {
      useLoadingIndicator: true,
      // Global ErrorHandler toast is not wired in the authentication app;
      // the page surfaces faults via useNotification (same pattern as login).
      errorHandling: 'unhandled'
    }
  );
}
